<template>
  <v-dialog :value="value" @input="updateValue" max-width="1200px">
    <v-card>
      <!-- Dialog Header -->
      <v-card-title class="red--text d-flex justify-space-between align-center dialog-header">
        <span class="title-text" color="white">User Management</span>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <!-- Dialog Content -->
      <v-card-text class="dialog-content">
        <!-- If no function is selected, show the cards -->
        <div v-if="!selectedFunction">
          <v-row dense>
            <v-col
              v-for="(func, index) in functionsList"
              :key="func"
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                class="function-card"
                outlined
                hover
                @click="selectFunction(func)"
              >
                <v-card-title
                  class="py-6 d-flex justify-center function-card-title"
                >
                  {{ formatFunctionName(func) }}
                </v-card-title>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <!-- Otherwise, show the data-table with custom columns -->
        <div v-else>
          <v-btn class="mb-4 back-btn" @click="backToFunctions">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to Functions
          </v-btn>
          <v-data-table
            :headers="currentTableHeaders"
            :items="users"
            :search="userSearch"
            class="elevation-2 styled-table"
            item-key="id"
          >
            <!-- Search Field -->
            <template v-slot:top>
              <v-text-field
                v-model="userSearch"
                label="Search Users"
                prepend-icon="mdi-magnify"
                single-line
                hide-details
                class="mb-4 search-field"
              ></v-text-field>
            </template>
            <!-- Email Column -->
            <template v-slot:item.email="{ item }">
              {{ item.email }}
            </template>
            <!-- Dynamic Checkbox Columns -->
            <template
              v-for="(colVal, index) in currentPlaceholderColumns"
              v-slot:[`item.${colVal}`]="{ item }"
            >
              <v-checkbox
                :key="`${item.id}-${colVal}`"
                :input-value="item[colVal]"
                @change="toggleUserColumn(item, colVal, $event)"
                hide-details
                dense
              />
            </template>
          </v-data-table>
        </div>
      </v-card-text>
      <!-- Dialog Actions -->
      <v-card-actions class="dialog-actions">
        <v-spacer></v-spacer>
        <v-btn color="red darken-1" text @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { firebaseApp } from '@/firebaseConfig';
export default {
  name: 'UserPermission',
  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      users: [],
      userSearch: '',
      functionsList: [
        'oldJobs',
        'jobReports',
        'deliveries',
        'returns',
        'purchaseOrders',
        'engineerQuotes',
        'salesOrderTracker',
        'awaitingSpares',
        'samples',
        'shipment',
        'serial',
        'sharedServices',
        'customerQuotes',
        'quoted'
      ],
      selectedFunction: null,
      columnsConfig: {
        oldJobs: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'View Line Items (Labour and Stock)',
          'View Supplier Info',
          'Override All',
        ],
        jobReports: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'Override All',
        ],
        deliveries: [
          'View in Activity',
          'View In Function',
          'Add Delivery',
          'Edit Delivery Card Details',
          'Edit Delivery Items',
          'Override',
        ],
        engineerQuotes: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'View Line Items (Labour and Stock)',
          'View Supplier Info',
          'Override All',
        ],
        salesOrderTracker: [
          'View in Functions',
          'View in Activity',
          'Add Button',
          'Edit Details',
          'Override All',
        ],
        awaitingSpares: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'Override All',
        ],
        samples: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'Override All',
        ],
        shipment: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'Override All',
        ],
        serial: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'Override All',
        ],
        returns: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'View Line Items (Labour and Stock)',
          'View Supplier Info',
          'Override All',
          'Placeholder for 8th Column?',
        ],
        purchaseOrders: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'View Line Items (Labour and Stock)',
          'View Supplier Info',
          'Override All',
          'Placeholder for 8th Column?',
        ],
        sharedServices: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'Override All'
        ],
        customerQuotes: [
        'View in Functions',
        'View in Activities',
        'Add Button',
        'Edit Details',
        'Override All',
      ],
        quoted: [
       'View in Functions',
       'View in Activities',
       'Edit Details',
       'Override All',
      ],
        default: [
          'View in Functions',
          'View in Activities',
          'Add Button',
          'Edit Details',
          'View Line Items (Labour and Stock)',
          'View Supplier Info',
          'Override All',
          'Placeholder for 8th Column?',
        ],
      },
    };
  },
  computed: {
    currentTableHeaders() {
      const cols =
        this.columnsConfig[this.selectedFunction] ||
        this.columnsConfig.default;
      const dynamicHeaders = cols.map((col, index) => ({
        text: col,
        value: `col${index + 1}`,
        sortable: false,
      }));
      return [{ text: 'Email', value: 'email' }, ...dynamicHeaders];
    },
    currentPlaceholderColumns() {
      const cols =
        this.columnsConfig[this.selectedFunction] ||
        this.columnsConfig.default;
      return cols.map((_, i) => `col${i + 1}`);
    },
  },
  methods: {
    updateValue(val) {
      this.$emit('input', val);
    },
    closeDialog() {
      this.$emit('input', false);
      this.selectedFunction = null;
    },
    formatFunctionName(func) {
      return func
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    },
    selectFunction(func) {
      this.selectedFunction = func;
      this.fetchUsers();
    },
    backToFunctions() {
      this.selectedFunction = null;
      this.userSearch = '';
    },
    async fetchUsers() {
      const db = getFirestore(firebaseApp);
      try {
        const querySnapshot = await getDocs(collection(db, 'users'));
        const list = querySnapshot.docs.map((document) => {
          const data = document.data();
          const userObj = {
            id: document.id,
            email: data.email || '',
            fullName: data.fullName || '',
            createdAt: data.createdAt || null,
            ...data,
          };
          if (this.selectedFunction) {
            const functionField = this.selectedFunction;
            const config =
              this.columnsConfig[functionField] ||
              this.columnsConfig.default;
            config.forEach((header, idx) => {
              const safeHeader = header
                .replace(/\s+/g, '_')
                .replace(/[\(\)?]/g, '');
              userObj[`col${idx + 1}`] =
                data[functionField] &&
                data[functionField][safeHeader] !== undefined
                  ? data[functionField][safeHeader]
                  : false;
            });
          }
          return userObj;
        });

        // Sort users alphabetically by email
        list.sort((a, b) => a.email.localeCompare(b.email));

        this.users = list;
        console.log(
          `Fetched users for ${this.selectedFunction || 'all'}:`,
          this.users
        );
      } catch (error) {
        console.error('Error fetching users:', error);
        this.$emit('alert', {
          message: `Error fetching users: ${error.message}`,
          type: 'error',
        });
      }
    },
    async updateUserColumn(userId, column, value) {
      const db = getFirestore(firebaseApp);
      try {
        const userRef = doc(db, 'users', userId);
        if (this.selectedFunction) {
          const functionField = this.selectedFunction;
          const colIndex = parseInt(column.replace('col', ''), 10) - 1;
          const config =
            this.columnsConfig[this.selectedFunction] ||
            this.columnsConfig.default;
          const header = config[colIndex];
          const safeHeader = header
            .replace(/\s+/g, '_')
            .replace(/[\(\)?]/g, '');

          let updateData = {};

          console.log(
            `Updating ${functionField}.${safeHeader} to ${value} for user ${userId}`
          );

          if (safeHeader === 'Override_All' && value === true) {
            config.forEach((hdr) => {
              const safeH = hdr
                .replace(/\s+/g, '_')
                .replace(/[\(\)?]/g, '');
              const fieldKey = `${functionField}.${safeH}`;
              updateData[fieldKey] = safeH === 'Override_All' ? true : false;
            });
          } else {
            updateData = {
              [`${functionField}.${safeHeader}`]: value,
            };
          }
          await updateDoc(userRef, updateData);
          console.log(
            `Successfully updated ${functionField}.${safeHeader} to ${value}`
          );
          // Update local user object
          const userItem = this.users.find((u) => u.id === userId);
          if (userItem) {
            userItem[column] = value;
            userItem[functionField] = userItem[functionField] || {};
            userItem[functionField][safeHeader] = value;
          }

          // Re-fetch users to ensure UI is in sync with Firestore
          await this.fetchUsers();

          this.$emit('alert', {
            message: `${functionField} permission updated successfully!`,
            type: 'success',
          });
        }
      } catch (error) {
        console.error('Error updating user column:', error);
        this.$emit('alert', {
          message: `Error updating user column: ${error.message}`,
          type: 'error',
        });
      }
    },
    toggleUserColumn(item, colVal, newValue) {
      const value = newValue === true;
      console.log(
        `Toggling ${this.selectedFunction}.${colVal} for user ${item.id} to ${value}`
      );
      this.updateUserColumn(item.id, colVal, value);
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.selectedFunction = null;
        this.fetchUsers();
      }
    },
  },
};
</script>
<style scoped>
/* Dialog Header */
.dialog-header {
  background-color: #ebe7e8;
  color: white;
  font-size: 18px;
  font-weight: bold;
  border-radius: 8px 8px 0 0;
  padding: 16px;
}
.title-text {
  font-size: 20px;
}
/* Dialog Content */
.dialog-content {
  padding: 20px;
  background-color: #f9f9f9;
}
/* Cards for functions */
.function-card {
  cursor: pointer;
  transition: transform 0.3s;
  text-align: center;
  min-height: 150px;
}
.function-card:hover {
  transform: translateY(-5px);
}
.function-card-title {
  font-weight: bold;
  text-align: center;
}
/* Table Styling */
.styled-table {
  border-radius: 8px;
  overflow: hidden;
  background: white;
}
.styled-table thead th {
  background-color: #ff1744;
  color: white;
  font-weight: bold;
  text-align: center;
}
.styled-table tbody tr {
  transition: background 0.3s;
}
.styled-table tbody tr:hover {
  background-color: #ffeaea;
}
/* Dialog Actions */
.dialog-actions {
  padding: 16px;
  background-color: #fff;
}
/* Input Fields */
.search-field {
  margin-bottom: 20px;
}
/* Back to Functions Button Styling */
.back-btn {
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ccc;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s, box-shadow 0.3s;
}
.back-btn:hover {
  background-color: #e0e0e0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}
</style>